import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  FormGroup,
  Checkbox,
  IconButton,
  debounce,
  Typography,
  CircularProgress,
  Grid,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Edit as EditIcon, Person as PersonIcon } from '@mui/icons-material';
import User from '../models/iUser';
import NewUserDataGrid from '../components/NewUserDataGrid';
import { addRoleToUser, getRoles, getUsersByRole, removeRoleFromUser } from '../providers/identityApi';
import { useOktaAuth } from '@okta/okta-react';

interface IUserRowModel {
  id: number;
  userId: string;
  name: string;
  roles: Array<string>;
  email: string;
}

const Users = () => {
  const { authState } = useOktaAuth();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const [areUsersLoading, setAreUsersLoading] = useState(true);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [addedUser, setAddedUser] = useState<User | null>();
  const [currentUser, setCurrentUser] = useState<IUserRowModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState(['']);

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  const [debouncedFirstName, setDebouncedFirstName] = useState<string>('');
  const [debouncedLastName, setDebouncedLastName] = useState<string>('');
  const [debouncedEmail, setDebouncedEmail] = useState<string>('');

  const debouncedSetFirstName = useCallback(debounce(setDebouncedFirstName, 300), []);
  const debouncedSetLastName = useCallback(debounce(setDebouncedLastName, 300), []);
  const debouncedSetEmail = useCallback(debounce(setDebouncedEmail, 300), []);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
    debouncedSetFirstName(e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
    debouncedSetLastName(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    debouncedSetEmail(e.target.value);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const rows: IUserRowModel[] = useMemo(() => {
    return users.map((user, index) => {
      return {
        id: index,
        userId: user.id,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        roles: user.roles,
      };
    });
  }, [users]);

  // Get the users
  const fetchUsers = async () => {
    const bearerToken = authState?.accessToken?.accessToken;
    if (bearerToken) {
      try {
        const rolesFromApi: Array<string> = await getRoles(bearerToken);
        setRoles(rolesFromApi);

        const userPromises = rolesFromApi.map((role) =>
          getUsersByRole(role, bearerToken).then((users) => users.map((user) => ({ ...user, role })))
        );
        const usersFromApi = await Promise.all(userPromises);

        const combinedUsers = usersFromApi.flat().reduce((acc: User[], user) => {
          const existingUser = acc.find((u) => u.id === user.id);
          if (existingUser) {
            existingUser.roles.push(user.role);
          } else {
            acc.push({ ...user, roles: [user.role] });
          }
          return acc;
        }, []);

        setUsers(combinedUsers);
        setAreUsersLoading(false);
      } catch (error) {
        toast.error('Error retrieving users: Please try again later');
      }
      setAreUsersLoading(false);
    }
  };

  const handleBulkActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleBulkActionClose = () => {
    setMenuAnchorEl(null);
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1,
      renderCell: (params) => <div>{params?.value?.join(', ')}</div>,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <Grid>
          <IconButton
            onClick={() => {
              setIsRolesModalOpen(true);
              setCurrentUser({ ...params.row });
            }}
            style={{ cursor: 'pointer' }}
          >
            <PersonIcon />
          </IconButton>
        </Grid>
      ),
    },
  ];

  const handleChangeUserRoles = async () => {
    const bearerToken = authState?.accessToken?.accessToken;
    if (bearerToken) {
      try {
        setIsLoading(true);
        const changedUser = users.find((u) => `${u.firstName} ${u.lastName}` === currentUser?.name);

        if (changedUser) {
          const addedRoles = currentUser?.roles.filter((role) => !changedUser.roles.includes(role));
          const deletedRoles = changedUser.roles.filter((role) => !currentUser?.roles.includes(role));

          if (addedRoles) {
            for (const role of addedRoles) {
              await addRoleToUser(changedUser.id, role, bearerToken);
            }
          }

          if (deletedRoles) {
            for (const role of deletedRoles) {
              await removeRoleFromUser(changedUser.id, role, bearerToken);
            }
          }
          await fetchUsers();
        }
      } catch (error) {
        toast.error('Error changing roles: please try again later');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteUsers = async () => {
    const bearerToken = authState?.accessToken?.accessToken;
    if (bearerToken) {
      try {
        setIsLoading(true);
        const selectedRows = rows.filter((row) => selectionModel.includes(row.id));

        await Promise.all(
          selectedRows.map(async (row) => {
            const user = users.find((u) => `${u.firstName} ${u.lastName}` === row?.name);

            if (user) {
              for (const role of user.roles) {
                await removeRoleFromUser(user.id, role, bearerToken);
              }
              await fetchUsers();
            }
          })
        );
      } catch {
        toast.error('Error deleting users: please try again later');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddUser = async () => {
    const bearerToken = authState?.accessToken?.accessToken;
    if (bearerToken) {
      setIsLoading(true);
      try {
        if (addedUser) {
          for (const role of addedUser.roles) {
            await addRoleToUser(addedUser.id, role, bearerToken);
          }
          await fetchUsers();
        }
      } catch {
        toast.error('Error: Could not add user, please try again later.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Stack gap={4} sx={{ padding: '2em', height: '100vh', width: '100%' }}>
      <Grid sx={{ display: 'flex', justifyContent: 'flex-end', gap: '2em' }}>
        <Button
          variant="outlined"
          color="primary"
          sx={{ color: 'var(--brady-blue)', borderColor: 'var(--brady-blue)' }}
          onClick={() => {
            setIsAddUserModalOpen(true);
          }}
        >
          Add New User
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ backgroundColor: 'var(--brady-blue)' }}
          onClick={handleBulkActionClick}
        >
          Bulk Action
        </Button>
        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleBulkActionClose}>
          <MenuItem
            onClick={() => {
              if (selectionModel.length > 0) {
                setIsDeleteModalOpen(true);
              }
            }}
          >
            Delete Users
          </MenuItem>
        </Menu>
      </Grid>
      {areUsersLoading || isLoading ? (
        <Grid
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <CircularProgress />
        </Grid>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          getRowHeight={() => 'auto'}
          onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
          disableRowSelectionOnClick
          sx={{
            height: '100vh',
            width: '100%',
            border: '1px solid #ccc',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      )}

      <Dialog open={isAddUserModalOpen} maxWidth="xl">
        <DialogTitle>Search For Users to Add</DialogTitle>
        <DialogContent
          style={{
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Stack gap={4}>
            <FormControl component="fieldset" style={{ width: '100%' }}>
              <TextField
                label="First Name"
                value={firstName}
                onChange={handleFirstNameChange}
                fullWidth
                margin="normal"
              />
              <TextField label="Last Name" value={lastName} onChange={handleLastNameChange} fullWidth margin="normal" />
              <TextField label="Email" value={email} onChange={handleEmailChange} fullWidth margin="normal" />
            </FormControl>
            <NewUserDataGrid
              firstName={debouncedFirstName}
              lastName={debouncedLastName}
              email={debouncedEmail}
              setAddedUser={setAddedUser}
            />
            <FormControl component="fieldset" style={{ width: '100%' }}>
              {addedUser && addedUser.roles && (
                <>
                  <Typography variant="h6">Select User Roles: </Typography>
                  {roles.map((role, index) => (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={addedUser.roles.some((u) => u === role)}
                          onChange={(_) => {
                            const updatedRoles = [...addedUser.roles];
                            const roleIndex = updatedRoles.findIndex((u) => u === role);
                            if (roleIndex >= 0) {
                              updatedRoles.splice(roleIndex, 1);
                            } else {
                              updatedRoles.push(role);
                            }
                            setAddedUser({
                              ...addedUser,
                              roles: updatedRoles,
                            });
                          }}
                        />
                      }
                      label={role}
                    />
                  ))}
                </>
              )}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFirstName('');
              setLastName('');
              setEmail('');
              setDebouncedFirstName('');
              setDebouncedLastName('');
              setDebouncedEmail('');
              setIsAddUserModalOpen(false);
              setAddedUser(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (addedUser && users.some((u) => u.id === addedUser.id)) {
                return;
              } else if (addedUser && addedUser.roles.length > 0) {
                setIsAddUserModalOpen(false);
                await handleAddUser();
              } else {
                return;
              }
              setFirstName('');
              setLastName('');
              setEmail('');
              setDebouncedFirstName('');
              setDebouncedLastName('');
              setDebouncedEmail('');
              setAddedUser(null);
            }}
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isRolesModalOpen}>
        <DialogTitle>Change Roles for {currentUser?.name}</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <Grid
              container
              direction="column"
              style={{
                maxHeight: '50vh',
                overflowY: 'auto',
                paddingLeft: '10px',
              }}
            >
              <FormGroup>
                {roles.map((role, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={currentUser?.roles.some((u) => u === role)}
                        onChange={(_) => {
                          if (currentUser) {
                            const updatedRoles = [...currentUser.roles];
                            const roleIndex = updatedRoles.findIndex((u) => u === role);
                            if (roleIndex >= 0) {
                              updatedRoles.splice(roleIndex, 1);
                            } else {
                              updatedRoles.push(role);
                            }
                            setCurrentUser({
                              ...currentUser,
                              roles: updatedRoles,
                            });
                          }
                        }}
                      />
                    }
                    label={role}
                  />
                ))}
              </FormGroup>
            </Grid>
          </FormControl>
        </DialogContent>

        {!isLoading && (
          <DialogActions>
            <Button
              onClick={() => {
                setIsRolesModalOpen(false);
              }}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setIsRolesModalOpen(false);
                await handleChangeUserRoles();
              }}
              color="primary"
            >
              Confirm
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog open={isDeleteModalOpen}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete all of the selected users? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteModalOpen(false);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsDeleteModalOpen(false);
              handleDeleteUsers();
            }}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default Users;
